/**
 * Container component responsible for wiring the workspace selector
 * to the risk profile state.
 *
 * It reads the available workspaces and selected workspace from Jotai,
 * persists workspace selection, and delegates rendering to
 * WorkspaceSelectorView.
 *
 * If an onSelect callback is provided, it is invoked after the selected
 * workspace has been updated.
 *
 * Pass `value` when the selection is owned elsewhere
 */

import { riskAvailableWorkspacesAtom, selectedRiskProfileWorkspaceAtom } from '@app/state/riskAtoms'
import { RiskWorkspace } from '@app/types/workspaceTypes'
import { useAtom, useAtomValue } from 'jotai'
import { useState } from 'react'
import WorkspaceSelectorView from './WorkspaceSelectorView'

export type WorkspaceSelectorProps = {
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  value?: RiskWorkspace | null
  onSelect?: (workspace: RiskWorkspace) => void
  location?: 'riskMap'
  className?: string
}

const WorkspaceSelector = ({
  label = '',
  placeholder = 'Select a Workspace',
  searchPlaceholder = 'Search workspaces',
  value,
  onSelect,
  location,
  className,
}: WorkspaceSelectorProps) => {
  const workspaces = useAtomValue(riskAvailableWorkspacesAtom)
  const [atomWorkspace, setSelectedWorkspace] = useAtom(selectedRiskProfileWorkspaceAtom)
  const [open, setOpen] = useState(false)

  const isControlled = value !== undefined
  const selectedWorkspace = isControlled ? value : atomWorkspace
  const workspacesData = workspaces.data ?? []

  const handleSelect = (workspace: RiskWorkspace) => {
    if (!isControlled) setSelectedWorkspace(workspace)
    onSelect?.(workspace)
    setOpen(false)
  }

  return (
    <WorkspaceSelectorView
      label={label}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      workspaces={workspacesData}
      selectedWorkspace={selectedWorkspace}
      isLoading={workspaces.isLoading}
      isError={workspaces.isError}
      open={open}
      onOpenChange={setOpen}
      onSelect={handleSelect}
      className={className}
      location={location}
    />
  )
}

export default WorkspaceSelector
